import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ShoppingBag, AlertTriangle, ArrowRight, ChevronDown, ChevronUp,
  RotateCcw, Check, CalendarDays,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import {
  getShoppingList, patchShoppingState, resetShoppingPurchased,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

/* ------------------------------ helpers -------------------------------- */

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const addDaysISO = (isoDate, days) => {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const RU_MONTHS = ["янв","февр","мар","апр","мая","июня","июля","авг","сент","окт","нояб","дек"];
const fmtShort = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${RU_MONTHS[d.getMonth()]}`;
};
const rangeLabel = (start, end) =>
  start === end ? fmtShort(start) : `${fmtShort(start)} — ${fmtShort(end)}`;

/** Human-friendly unit conversion from backend normalized (g/ml/pcs/cm) values.
 *  Returns { value: Number, unit: string } — value is rounded pragmatically.
 */
function humanize(baseUnit, normalizedQty) {
  const n = Number(normalizedQty);
  if (!Number.isFinite(n)) return { value: 0, unit: baseUnit };
  const abs = Math.abs(n);
  if (baseUnit === "g") {
    if (abs >= 1000) return { value: n / 1000, unit: "кг" };
    return { value: n, unit: "г" };
  }
  if (baseUnit === "ml") {
    if (abs >= 1000) return { value: n / 1000, unit: "л" };
    return { value: n, unit: "мл" };
  }
  if (baseUnit === "cm") {
    if (abs >= 100) return { value: n / 100, unit: "м" };
    return { value: n, unit: "см" };
  }
  if (baseUnit === "pcs") return { value: n, unit: "шт." };
  return { value: n, unit: baseUnit };
}
/** Pragmatic number formatter: whole → no decimals; 1..1000 → up to 2 dp; small → 3 sig. */
function fmtNum(n) {
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  if (Math.abs(n) >= 100 && Number.isInteger(n * 100)) {
    return n.toLocaleString("ru-RU", { maximumFractionDigits: 1 });
  }
  if (Math.abs(n) < 0.01) return n.toPrecision(2);
  const rounded = Math.round(n * 100) / 100;
  return rounded.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}
function humanQty(baseUnit, normalizedQty) {
  const { value, unit } = humanize(baseUnit, normalizedQty);
  return `${fmtNum(value)} ${unit}`;
}
/** Convert a human-facing value+unit back to the base unit (for PATCH). */
function toBaseUnit(baseUnit, humanValue, humanUnit) {
  const v = Number(humanValue);
  if (!Number.isFinite(v)) return null;
  if (baseUnit === "g") return humanUnit === "кг" ? v * 1000 : v;
  if (baseUnit === "ml") return humanUnit === "л" ? v * 1000 : v;
  if (baseUnit === "cm") return humanUnit === "м" ? v * 100 : v;
  return v; // pcs
}
/** Preferred input unit for editing "У меня есть" — matches the display unit
 *  that the required quantity is shown in, so numbers feel natural. */
function preferredInputUnit(baseUnit, referenceQty) {
  const { unit } = humanize(baseUnit, referenceQty);
  return unit;
}

/* ------------------------------- period -------------------------------- */

const PRESETS = [
  { id: "today",  label: "Сегодня",  days: 0 },
  { id: "3d",     label: "3 дня",    days: 2 },
  { id: "7d",     label: "7 дней",   days: 6 },
  { id: "14d",    label: "14 дней",  days: 13 },
  { id: "custom", label: "Свой период", days: null },
];

function PeriodPicker({ preset, onPreset, start, end, onCustom, ordersCount }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3 md:px-5 md:py-4"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-purchases-period">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onPreset(p.id)}
              className="h-9 px-3.5 rounded-full text-[12.5px] transition"
              style={{
                background: active ? "#4A3728" : "#F7F1E7",
                color: active ? "#F7EFE0" : "#4A3728",
                boxShadow: active ? "none" : "inset 0 0 0 1px #EFE4D0",
              }}
              data-testid={`mo-period-${p.id}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {preset === "custom" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: "#4A3728" }}>
          <label className="flex items-center gap-1.5">
            <CalendarDays size={13} color="#8B6F4B" />
            <span>С</span>
            <input type="date" value={start} onChange={(e) => onCustom(e.target.value, end)}
                   className="h-9 px-2 rounded-lg bg-white outline-none text-[12.5px]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-period-custom-start" />
          </label>
          <label className="flex items-center gap-1.5">
            <span>по</span>
            <input type="date" value={end} onChange={(e) => onCustom(start, e.target.value)}
                   className="h-9 px-2 rounded-lg bg-white outline-none text-[12.5px]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-period-custom-end" />
          </label>
        </div>
      )}

      <div className="mt-3 text-[12.5px]" style={{ color: "#8B6F4B" }} data-testid="mo-period-summary">
        <span style={{ color: "#4A3728" }}>{rangeLabel(start, end)}</span>
        <span> · </span>
        <span>
          {ordersCount === 0 ? "нет заказов" : `${ordersCount} ${
            ordersCount === 1 ? "заказ" : ordersCount < 5 ? "заказа" : "заказов"
          }`}
        </span>
      </div>
    </div>
  );
}

/* ------------------------ unresolved warning block --------------------- */

function UnresolvedBlock({ items }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (!items.length) return null;

  const n = items.length;
  const noun = n === 1 ? "позиции" : n < 5 ? "позициям" : "позициям";

  return (
    <div className="rounded-2xl px-4 py-4 md:px-5 md:py-4 mb-4"
         style={{ background: "#FDF3D9", boxShadow: "inset 0 0 0 1px #E9D28F" }}
         data-testid="mo-purchases-unresolved">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full grid place-items-center shrink-0"
             style={{ background: "#F1D07C", color: "#5A3E10" }}>
          <AlertTriangle size={16} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] leading-tight" style={{ color: "#5A3E10" }}>
            По {n} {noun} ещё не готов расчёт для закупки
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#8A6B2B" }}>
            Этих позиций пока нет в списке покупок — сначала рассчитайте себестоимость.
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)}
                className="h-9 px-3 rounded-full text-[12px] shrink-0 flex items-center gap-1"
                style={{ background: "#FFFFFF", color: "#5A3E10",
                         boxShadow: "inset 0 0 0 1px #E9D28F" }}
                data-testid="mo-purchases-unresolved-toggle">
          {open ? "Скрыть" : "Посмотреть"}
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {open && (
        <ul className="mt-3 space-y-2" data-testid="mo-purchases-unresolved-list">
          {items.map((it) => {
            const isDraft = it.kind === "draft_costing";
            const target = isDraft
              ? `/master-order/costings/${it.costing_id}`
              : `/master-order/orders/${it.order_id}#item-${it.order_item_id}`;
            return (
              <li key={`${it.order_id}-${it.order_item_id}`}>
                <button
                  onClick={() => nav(target)}
                  className="w-full flex items-center gap-3 rounded-xl bg-white px-3.5 py-3 text-left transition hover:bg-[#FDF7EC]"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-purchases-unresolved-row"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] truncate">
                      Заказ № {it.order_number}
                      {it.product_type_name && (
                        <span style={{ color: "#8B6F4B" }}> · {it.product_type_name}</span>
                      )}
                    </div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                      {isDraft ? "Расчёт ещё в работе" : "Для этой позиции ещё нет расчёта для закупки"}
                    </div>
                  </div>
                  <ArrowRight size={14} color="#8B6F4B" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* --------------------------- one shopping row -------------------------- */

function InlineHaveInput({ entry, canWrite, onSave }) {
  const displayUnit = preferredInputUnit(entry.base_unit, entry.required_quantity);
  const { value: haveValue } = humanize(entry.base_unit, entry.available_quantity);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(fmtNum(haveValue).replace(",", "."));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(fmtNum(haveValue).replace(",", "."));
  }, [haveValue, editing]);

  const commit = async () => {
    const cleaned = draft.replace(",", ".").trim();
    const n = cleaned === "" ? 0 : Number(cleaned);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Введите неотрицательное число");
      setDraft(fmtNum(haveValue).replace(",", "."));
      setEditing(false);
      return;
    }
    const base = toBaseUnit(entry.base_unit, n, displayUnit);
    if (base === null) { setEditing(false); return; }
    if (Number(base) === Number(entry.available_quantity)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave({ available_quantity: String(base) });
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (!canWrite || !editing) {
    return (
      <button
        onClick={() => canWrite && setEditing(true)}
        className="text-left inline-flex items-baseline gap-1"
        style={{ color: canWrite ? "#4A3728" : "#8B6F4B", cursor: canWrite ? "text" : "default" }}
        data-testid="mo-purchases-have-view"
      >
        <span className="text-[12.5px] tabular-nums">
          {humanQty(entry.base_unit, entry.available_quantity)}
        </span>
        {canWrite && <span className="text-[10px]" style={{ color: "#B9A692" }}>изменить</span>}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        onBlur={commit}
        disabled={saving}
        className="h-8 w-20 px-2 rounded-lg bg-white outline-none text-[12.5px] tabular-nums"
        style={{ boxShadow: "inset 0 0 0 1px #C7A574", color: "#2A1D14" }}
        data-testid="mo-purchases-have-input"
      />
      <span className="text-[11.5px]" style={{ color: "#8B6F4B" }}>{displayUnit}</span>
    </span>
  );
}

function EntryRow({ entry, canWrite, period, onChanged }) {
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const patchState = useCallback(async (payload) => {
    setSaving(true);
    try {
      await patchShoppingState({
        entity_type: entry.entity_type,
        entity_id: entry.entity_id,
        period_start: period.start,
        period_end: period.end,
        payload,
      });
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }, [entry.entity_type, entry.entity_id, period.start, period.end, onChanged]);

  const togglePurchased = () => patchState({ purchased: !entry.purchased });

  const purchasedTone = entry.purchased ? { opacity: 0.72 } : {};

  return (
    <div
      className="rounded-2xl bg-white px-4 py-3.5 md:px-5 md:py-4"
      style={{
        boxShadow: entry.purchased
          ? "inset 0 0 0 1px #E4D9C4"
          : "inset 0 0 0 1px #EFE4D0",
        ...purchasedTone,
      }}
      data-testid="mo-purchases-row"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        {/* Name */}
        <div className="min-w-0 md:flex-1">
          <div className="text-[14.5px] md:text-[15px] leading-snug truncate"
               style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}
               data-testid="mo-purchases-row-name">
            {entry.name}
          </div>
          <div className="mt-0.5 text-[11.5px]" style={{ color: "#8B6F4B" }}>
            {entry.sources.length === 1
              ? "Для 1 заказа"
              : `Для ${entry.sources.length} заказ${entry.sources.length < 5 ? "ов" : "ов"}`}
          </div>
        </div>

        {/* Metrics on desktop: needed, have, to buy */}
        <div className="grid grid-cols-3 gap-3 md:gap-5 md:w-[420px] shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>Нужно</div>
            <div className="text-[13px] tabular-nums" style={{ color: "#4A3728" }}
                 data-testid="mo-purchases-row-required">
              {humanQty(entry.base_unit, entry.required_quantity)}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>У меня есть</div>
            <div data-testid="mo-purchases-row-have">
              <InlineHaveInput entry={entry} canWrite={canWrite} onSave={patchState} />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>Докупить</div>
            <div
              className="text-[14px] md:text-[15px] tabular-nums"
              style={{ color: entry.purchased ? "#8B6F4B" : Number(entry.to_buy) === 0 ? "#4A3728" : "#7A4A16", fontWeight: 600 }}
              data-testid="mo-purchases-row-tobuy"
            >
              {humanQty(entry.base_unit, entry.to_buy)}
            </div>
          </div>
        </div>

        {/* Purchased toggle + expand */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={togglePurchased}
            disabled={!canWrite || saving}
            className="h-9 px-3 rounded-full text-[12px] flex items-center gap-1.5 transition"
            style={{
              background: entry.purchased ? "#4A3728" : "#F7F1E7",
              color: entry.purchased ? "#F7EFE0" : "#4A3728",
              boxShadow: entry.purchased ? "none" : "inset 0 0 0 1px #EFE4D0",
              opacity: !canWrite ? 0.5 : 1,
              cursor: !canWrite ? "default" : "pointer",
            }}
            data-testid="mo-purchases-row-purchased"
          >
            {entry.purchased ? <Check size={13} /> : <div className="w-3.5 h-3.5 rounded-full" style={{ boxShadow: "inset 0 0 0 1px #C7A574" }} />}
            {entry.purchased ? "Куплено" : "Куплено?"}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="h-9 w-9 rounded-full grid place-items-center transition"
            style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
            data-testid="mo-purchases-row-expand"
            aria-label={expanded ? "Скрыть источники" : "Показать источники"}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #E4D9C4" }} data-testid="mo-purchases-row-sources">
          <div className="text-[11px] tracking-[0.14em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            Из каких заказов
          </div>
          <ul className="space-y-1.5">
            {entry.sources.map((s, idx) => (
              <li key={`${s.order_item_id}-${idx}`}>
                <button
                  onClick={() => nav(`/master-order/orders/${s.order_id}`)}
                  className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-[12.5px] text-left transition hover:bg-[#FDF7EC]"
                  style={{ color: "#2A1D14" }}
                  data-testid="mo-purchases-row-source-link"
                >
                  <span className="tabular-nums" style={{ color: "#4A3728" }}>№ {s.order_number}</span>
                  <span style={{ color: "#8B6F4B" }}>·</span>
                  <span className="tabular-nums" style={{ color: "#4A3728" }}>
                    {humanQty(entry.base_unit, s.quantity_contribution)}
                  </span>
                  {s.order_item_quantity > 1 && (
                    <span style={{ color: "#8B6F4B" }}> · {s.order_item_quantity} шт. в заказе</span>
                  )}
                  <ArrowRight size={12} color="#8B6F4B" className="ml-auto" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- section ------------------------------- */

function Section({ title, entries, canWrite, period, onChanged, filter }) {
  const visible = useMemo(() => {
    if (filter === "unpurchased") return entries.filter((e) => !e.purchased);
    if (filter === "purchased")   return entries.filter((e) => e.purchased);
    return entries;
  }, [entries, filter]);

  const totalToBuyByCur = useMemo(() => {
    const byCur = {};
    for (const e of visible) {
      for (const c of e.costs_by_currency) {
        const unitCost = Number(c.unit_cost);
        const toBuy = Number(e.to_buy);
        if (Number.isFinite(unitCost) && Number.isFinite(toBuy)) {
          byCur[c.currency_code] = (byCur[c.currency_code] || 0) + unitCost * toBuy;
        }
      }
    }
    return byCur;
  }, [visible]);

  return (
    <section className="mt-5" data-testid={`mo-purchases-section-${title === "Ингредиенты" ? "ingredients" : "packaging"}`}>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h2 className="text-[18px] md:text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          {title}
        </h2>
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
          {visible.length === 0
            ? "нет позиций"
            : `${visible.length} ${visible.length === 1 ? "позиция" : visible.length < 5 ? "позиции" : "позиций"}`}
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="rounded-2xl bg-white px-4 py-6 text-center text-[12.5px]"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B6F4B" }}>
          Здесь пока пусто.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((entry) => (
            <EntryRow
              key={`${entry.entity_type}-${entry.entity_id}`}
              entry={entry}
              canWrite={canWrite}
              period={period}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
      {Object.keys(totalToBuyByCur).length > 0 && (
        <div className="mt-2 px-2 text-[11.5px]" style={{ color: "#8B6F4B" }}
             data-testid="mo-purchases-section-total">
          Итого к закупке:{" "}
          {Object.entries(totalToBuyByCur).map(([cur, sum], i, arr) => (
            <span key={cur}>
              <span style={{ color: "#4A3728" }}>{fmtNum(sum)} {cur}</span>
              {i < arr.length - 1 && " · "}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/* --------------------------------- page --------------------------------- */

function purchasedCount(items) {
  return items.reduce((n, e) => n + (e.purchased ? 1 : 0), 0);
}

export default function MOPurchases() {
  const { mode, loading: accessLoading } = useMOAccess();
  const canWrite = isWritable(mode);

  const [preset, setPreset] = useState("7d");
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(addDaysISO(todayISO(), 6));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [resetting, setResetting] = useState(false);

  const applyPreset = (id) => {
    setPreset(id);
    const today = todayISO();
    const p = PRESETS.find((x) => x.id === id);
    if (!p || p.days === null) return; // custom — user picks dates
    setStart(today);
    setEnd(addDaysISO(today, p.days));
  };
  const setCustom = (s, e) => {
    setStart(s);
    setEnd(e);
    setPreset("custom");
  };

  const load = useCallback(async () => {
    if (start > end) return;
    setLoading(true);
    try {
      const body = await getShoppingList({ period_start: start, period_end: end });
      setData(body);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const doReset = async () => {
    if (!canWrite) return;
    setResetting(true);
    try {
      const { modified_count } = await resetShoppingPurchased({ period_start: start, period_end: end });
      toast.success(modified_count > 0 ? `Сброшено «Куплено» у ${modified_count} позиций` : "Нечего сбрасывать");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  const ingredients = data?.ingredients || [];
  const packaging = data?.packaging || [];
  const unresolved = data?.unresolved || [];
  const purchasedTotal = purchasedCount(ingredients) + purchasedCount(packaging);

  const body = (() => {
    if (accessLoading) return <LoadingBlock label="Проверяем доступ..." />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;

    return (
      <>
        <PeriodPicker
          preset={preset}
          onPreset={applyPreset}
          start={start}
          end={end}
          onCustom={setCustom}
          ordersCount={data?.summary?.orders_in_period ?? 0}
        />

        {loading ? (
          <div className="mt-4"><LoadingBlock label="Собираем список..." /></div>
        ) : (
          <>
            <div className="mt-4">
              <UnresolvedBlock items={unresolved} />
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-1 mb-1 px-1"
                 data-testid="mo-purchases-filter-bar">
              {[
                { id: "all",         label: "Все" },
                { id: "unpurchased", label: "Не куплено" },
                { id: "purchased",   label: "Куплено" },
              ].map((f) => {
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className="h-8 px-3 rounded-full text-[12px] transition"
                    style={{
                      background: active ? "#EADFCF" : "transparent",
                      color: active ? "#2A1D14" : "#8B6F4B",
                      boxShadow: active ? "inset 0 0 0 1px #C7A574" : "inset 0 0 0 1px transparent",
                    }}
                    data-testid={`mo-purchases-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                );
              })}

              <div className="ml-auto flex items-center gap-2">
                {purchasedTotal > 0 && canWrite && (
                  <button
                    onClick={doReset}
                    disabled={resetting}
                    className="h-9 px-3 rounded-full text-[12px] flex items-center gap-1.5 transition"
                    style={{ background: "#FFFFFF", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-purchases-reset"
                  >
                    <RotateCcw size={13} />
                    {resetting ? "Сбрасываем..." : "Сбросить куплено"}
                  </button>
                )}
              </div>
            </div>

            {ingredients.length === 0 && packaging.length === 0 && unresolved.length === 0 && (
              <div className="mt-4 rounded-2xl bg-white p-8 md:p-10 text-center"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-purchases-empty">
                <div className="mx-auto w-12 h-12 rounded-full grid place-items-center mb-3"
                     style={{ background: "#EADFCF", color: "#4A3728" }}>
                  <ShoppingBag size={20} strokeWidth={1.7} />
                </div>
                <div className="text-[16px] md:text-[18px]"
                     style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                     data-testid="mo-purchases-empty-title">
                  На выбранный период заказов пока нет
                </div>
                <div className="text-[12.5px] mt-1.5 mx-auto max-w-md" style={{ color: "#8B6F4B" }}
                     data-testid="mo-purchases-empty-subtitle">
                  Когда появятся заказы с готовыми расчётами, список закупок сформируется автоматически.
                </div>
                <div className="mt-4 mx-auto max-w-md text-[11.5px] leading-relaxed"
                     style={{ color: "#8B6F4B" }} data-testid="mo-purchases-empty-flow">
                  Список закупок формируется автоматически из ваших заказов.
                  <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1"
                       style={{ color: "#4A3728" }}>
                    <span>Заказ</span><ArrowRight size={11} color="#B9A692" />
                    <span>расчёт</span><ArrowRight size={11} color="#B9A692" />
                    <span>ингредиенты и упаковка</span><ArrowRight size={11} color="#B9A692" />
                    <span>список закупок</span>
                  </div>
                </div>
              </div>
            )}

            {ingredients.length === 0 && packaging.length === 0 && unresolved.length > 0 && (
              <div className="mt-4 rounded-2xl bg-white p-8 md:p-10 text-center"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-purchases-empty-unresolved">
                <div className="mx-auto w-12 h-12 rounded-full grid place-items-center mb-3"
                     style={{ background: "#F5EBD8", color: "#7A4A16" }}>
                  <ShoppingBag size={20} strokeWidth={1.7} />
                </div>
                <div className="text-[16px] md:text-[18px]"
                     style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                     data-testid="mo-purchases-empty-unresolved-title">
                  По этим заказам ещё не готов расчёт для закупки
                </div>
                <div className="text-[12.5px] mt-1.5 mx-auto max-w-md" style={{ color: "#8B6F4B" }}
                     data-testid="mo-purchases-empty-unresolved-subtitle">
                  Когда расчёты будут готовы, ингредиенты и упаковка появятся здесь автоматически.
                </div>
              </div>
            )}

            <Section title="Ингредиенты" entries={ingredients}
                     canWrite={canWrite} period={{ start, end }} onChanged={load} filter={filter} />
            <Section title="Упаковка" entries={packaging}
                     canWrite={canWrite} period={{ start, end }} onChanged={load} filter={filter} />
          </>
        )}
      </>
    );
  })();

  return (
    <MOShell active="purchases" title="Запасы и закупка"
             subtitle="Что мне нужно купить для предстоящих заказов?">
      {body}
    </MOShell>
  );
}
